import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleAppiError, U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG, type U3141InitLU } from "./U3141Service";
import { U3141ContextProvider } from "./U3141Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

// Namespace constant and roles definition
export const NS = "u3141";

// Definition des rôles pour l'unité logique U3141
export const U3141ROLES = {
  READ: "U3141:READ",
  ADD: "U3141:ADD",
  EDIT: "U3141:EDIT",
  DELETE: "U3141:DELETE",
  AUDITS: "U3141:AUDITS",
  EXPORT: "U3141:EXPORT",
  /** Rôle requis pour « Récupérer l'historique cadastral » (job payant). */
  REGFON: "U3141:REGFON",
  /** Préréglages barre d’outils (éditeur PDF annotations) : créer / modifier / supprimer des modèles. */
  EDITPRESET: "U3141:EDITPRESET",
} as const;
export type U3141ROLES = (typeof U3141ROLES)[keyof typeof U3141ROLES];

// Main component for the U3141 logical unit
const U3141: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3141InitLU>({
    launchSearch: false,
    hideSearch: true,
    nbRecordPerPage: 50,
    addableDocumentTypes: [...U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG],
    singleSlotDocumentTypes: ['rfLotDetailIndex'],
    lotDetailOperationTypes: [],
    siteAppliSFoncierVersionUrl: null,
  });

  // charge fichiers de traduction
  useEffect(() => {
    i18next.loadNamespaces(["u3141", "u3140"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  // Gestion de l'initialisation de l'unité logique
  const handleInitLU = useU2000InitLUHandlers<U3141InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3141InitLU) => {
      setInitLUData(initLU);
      setInitLUReady(true);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  // Initialisation de l'unité logique au montage du composant et apres quelques changements de langue
  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  // Set LU title and breadcrumb
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);
    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3141ContextProvider initLU={initLUData}>
      <Outlet />
    </U3141ContextProvider>
  );
};

export default U3141;
